import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { regionService } from '../../services/regionService';
import { toast } from 'react-toastify';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const RegionSchema = Yup.object().shape({
  name: Yup.string().required('Bölge adı gereklidir'),
  category: Yup.string().required('Kategori seçimi gereklidir'),
  parentRegionId: Yup.mixed().nullable().transform(value => (value === '' ? null : Number(value))),
  googleMapLink: Yup.string().url('Geçerli bir URL giriniz').nullable(),
});

const RegionCreate = () => {
  const navigate = useNavigate();
  const [parentRegions, setParentRegions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchParentRegions();
  }, []);

  const fetchParentRegions = async () => {
    try {
      const response = await regionService.getRegions();
      setParentRegions(response.data.regions);
    } catch (error) {
      toast.error('Üst bölgeler yüklenirken bir hata oluştu');
      console.error('Error fetching parent regions:', error);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);
    try {
      await regionService.createRegion(values);
      toast.success('Bölge başarıyla oluşturuldu');
      navigate('/regions');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Bölge oluşturulurken bir hata oluştu');
      setSubmitting(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Yeni Bölge Ekle</h1>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/regions"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" aria-hidden="true" />
            Geri Dön
          </Link>
        </div>
      </div>

      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Bölge Bilgileri</h3>
            <p className="mt-1 text-sm text-gray-500">
              Yeni bir bölge eklemek için gerekli bilgileri doldurun.
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <Formik
              initialValues={{
                name: '',
                category: '',
                parentRegionId: '',
                googleMapLink: '',
                description: ''
              }}
              validationSchema={RegionSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form>
                  <div className="space-y-6">
                    {/* Bölge Adı */}
                    <div>
                      <label htmlFor="name" className="form-label">
                        Bölge Adı *
                      </label>
                      <Field
                        type="text"
                        name="name"
                        id="name"
                        className={`form-input ${
                          errors.name && touched.name ? 'form-input-error' : ''
                        }`}
                      />
                      <ErrorMessage name="name" component="div" className="form-error" />
                    </div>

                    {/* Kategori */}
                    <div>
                      <label htmlFor="category" className="form-label">
                        Kategori *
                      </label>
                      <Field
                        as="select"
                        name="category"
                        id="category"
                        className={`form-select ${
                          errors.category && touched.category ? 'form-input-error' : ''
                        }`}
                      >
                        <option value="">Kategori Seçin</option>
                        <option value="airport">Havalimanı</option>
                        <option value="city">Şehir</option>
                        <option value="district">İlçe</option>
                        <option value="hotel">Otel Bölgesi</option>
                      </Field>
                      <ErrorMessage name="category" component="div" className="form-error" />
                    </div>

                    {/* Üst Bölge */}
                    <div>
                      <label htmlFor="parentRegionId" className="form-label">
                        Üst Bölge
                      </label>
                      <Field
                        as="select"
                        name="parentRegionId"
                        id="parentRegionId"
                        className={`form-select ${
                          errors.parentRegionId && touched.parentRegionId ? 'form-input-error' : ''
                        }`}
                      >
                        <option value="">Üst Bölge Seçin</option>
                        {parentRegions.map((region) => (
                          <option key={region.id} value={region.id}>
                            {region.name} ({region.category})
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name="parentRegionId" component="div" className="form-error" />
                    </div>

                    {/* Google Harita Linki */}
                    <div>
                      <label htmlFor="googleMapLink" className="form-label">
                        Google Harita Linki
                      </label>
                      <Field
                        type="text"
                        name="googleMapLink"
                        id="googleMapLink"
                        className={`form-input ${
                          errors.googleMapLink && touched.googleMapLink ? 'form-input-error' : ''
                        }`}
                        placeholder="https://maps.google.com/..."
                      />
                      <ErrorMessage name="googleMapLink" component="div" className="form-error" />
                    </div>

                    {/* Açıklama */}
                    <div>
                      <label htmlFor="description" className="form-label">
                        Açıklama
                      </label>
                      <Field
                        as="textarea"
                        name="description"
                        id="description"
                        rows={3}
                        className="form-textarea"
                      />
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end space-x-3">
                    <Link
                      to="/regions"
                      className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                    >
                      İptal
                    </Link>
                    <button
                      type="submit"
                      disabled={isSubmitting || isLoading}
                      className="px-4 py-2 bg-primary-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {isLoading || isSubmitting ? (
                        <div className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Kaydediliyor...</span>
                        </div>
                      ) : (
                        'Kaydet'
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionCreate;
